import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { AvailabilityZoneSelect } from '@/components/selectors/availabilityZoneSelect';
import { GcpMachineTypeSelect } from '@/components/selectors/gcpMachineTypeSelect';
import { GcpNetworkSelect } from '@/components/selectors/gcpNetworkSelect';
import { GcpSubnetSelect } from '@/components/selectors/gcpSubnetSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { TransactionalTextField } from '@/components/shared/transactionalTextField';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import { AuthorizationTooltip } from '@/components/tooltips/authorizationTooltip';
import type { StepProps } from '@/components/wizard/StepProps';
import { FEATURES_SUPPORT_VERSIONS } from '@/configs/featureConfig';
import { useIsAccountFeatureSupported } from '@/utils/useIsAccountFeatureSupported';

import { StepContainer } from '../../../wizard/StepContainer';
import type { RestoreGcpVMState } from '../../restoreGcpVMWizard';
import { EonTagRemark } from '../eonTagRemark';
import { StartInstanceAfterRestoreToggle } from '../startInstanceAfterRestoreToggle';

export const GcpVmConfiguration = (props: StepProps<RestoreGcpVMState>) => {
  const isAccountFeatureSupported = useIsAccountFeatureSupported();

  const onNextClick = async () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };

  const isFeatureSupported =
    props.wizardState.restoreAccount &&
    isAccountFeatureSupported(
      FEATURES_SUPPORT_VERSIONS.GCP_START_INSTANCE_AFTER_RESTORE,
      props.wizardState.restoreAccount
    );

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      onBackClick={props.back}
      canGoNext={() =>
        !!props.wizardState.vmName &&
        !!props.wizardState.subnetName &&
        !!props.wizardState.regionName &&
        !!props.wizardState.machineType &&
        !!props.wizardState.zone
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
        />
      }
      navigationComponent={<EonTagRemark provider={Provider.Gcp} />}
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>Machine name</FormLabel>
            <TransactionalTextField
              initValue={props.wizardState.vmName}
              onChange={(vmName) =>
                props.setWizardState((state) => ({ ...state, vmName }))
              }
            />
          </FormControl>
          <RestoreRegionSelect
            isRequired
            cloudProvider={Provider.Gcp}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(regionName) =>
              props.setWizardState((state) => ({
                ...state,
                zone: undefined,
                subnetName: undefined,
                networkName: undefined,
                networkHostProject: undefined,
                isSharedNetwork: undefined,
                regionName,
              }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <AvailabilityZoneSelect
            isRequired
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            availabilityZone={props.wizardState.zone}
            cloudProvider={Provider.Gcp}
            onChange={(zone) =>
              props.setWizardState((state) => ({
                ...state,
                zone,
              }))
            }
          />
          <GcpMachineTypeSelect
            machineType={props.wizardState.machineType}
            accountId={props.wizardState.restoreAccount?.id}
            zone={props.wizardState.zone}
            onChange={(machineType) =>
              props.setWizardState((state) => ({
                ...state,
                machineType: machineType || '',
              }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <GcpNetworkSelect
            title='Network'
            disabledItems={[]}
            accountId={props.wizardState.restoreAccount?.id || ''}
            network={props.wizardState.network}
            onChange={(network) => {
              props.setWizardState((state) => ({
                ...state,
                network,
                subnetName: undefined,
              }));
            }}
          />
          <FormControl size='small' className='flex-1'>
            <FormLabel>Subnetwork</FormLabel>
            <GcpSubnetSelect
              accountId={props.wizardState.restoreAccount?.id || ''}
              region={props.wizardState.regionName || ''}
              network={props.wizardState.network}
              subnetName={props.wizardState.subnetName || ''}
              onChange={(subnetName) =>
                props.setWizardState((state) => ({
                  ...state,
                  subnetName: subnetName,
                }))
              }
            />
          </FormControl>
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(keepOriginalTags) =>
            props.setWizardState((state) => ({ ...state, keepOriginalTags }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
          provider={Provider.Gcp}
        />
      </CardContent>
      <Divider />
      <StartInstanceAfterRestoreToggle
        checked={props.wizardState.startInstanceAfterRestore}
        disabled={!isFeatureSupported}
        onChange={() => {
          props.setWizardState((state) => ({
            ...state,
            startInstanceAfterRestore: !state.startInstanceAfterRestore,
          }));
        }}
        switchWrapper={(sw) => (
          <AuthorizationTooltip isFeatureSupported={isFeatureSupported}>
            {sw}
          </AuthorizationTooltip>
        )}
      />
      <Divider />
    </StepContainer>
  );
};
